// src/modules/moduleDashboards/components/WidgetCard.jsx
// Presentational renderer for a single built-dashboard widget. The backend render
// endpoint returns { kind, chartType, title, route, data } in a generic shape; this
// maps it to a visual.
//
//   kpi      a headline figure, with optional delta vs the previous period and a
//            sparkline of the same measure over time
//   metrics  a panel of related figures (statement summaries, health checks)
//   table    a statement-style table with an optional summary row
//   list     recent records
//   chart    line / area / column / bar / pie / donut, single or multi-series
//   shortcut a button that routes somewhere
//
// Charts use @ant-design/charts on a colour-blind-validated categorical palette
// (worst adjacent CVD ΔE 9.1, normal-vision ΔE 19.6 on this surface). Because three
// of those hues sit under 3:1 contrast, every chart ships the relief the palette
// requires: a legend whenever there are two or more series, tooltips, and a
// "view as table" twin so no value is reachable by colour alone. An error boundary
// falls back to lightweight CSS bars so a chart-lib hiccup never blanks a card.
import React, { useMemo, useState } from 'react';
import { Card, List, Tag, Button, Empty, Typography, Table, Tooltip, Space, Badge, message } from 'antd';
import { useNavigate } from 'react-router-dom';
import * as Icons from '@ant-design/icons';
import {
  ArrowRightOutlined, ArrowUpOutlined, ArrowDownOutlined, CheckOutlined, CloseOutlined,
  BarChartOutlined, TableOutlined, CheckCircleFilled, WarningFilled, InfoCircleFilled,
  EyeOutlined, InboxOutlined,
} from '@ant-design/icons';
import { Line, Area, Column, Bar, Pie } from '@ant-design/charts';
import { ActionType, ModuleType, PermissionButton } from '../../../components';
import ApprovalActionModal from '../../approvals/components/ApprovalActionModal';
import { progressApprovalInstance, markApprovalViewed } from '../../approvals/api';
import EodControlWidget from './actions/EodControlWidget';
import IbctInboxWidget from './actions/IbctInboxWidget';

const { Text } = Typography;

/* ── Design tokens ─────────────────────────────────────────
 * Categorical hues in FIXED slot order — never cycled, never reassigned by rank,
 * so a series keeps its colour when the data behind it changes. Past 8 classes we
 * fold the tail into "Other" rather than inventing a 9th hue.
 */
const SERIES = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#4a3aa7', '#e34948'];
const SURFACE = '#ffffff';
const INK = { primary: 'rgba(0,0,0,0.88)', secondary: 'rgba(0,0,0,0.55)', muted: 'rgba(0,0,0,0.35)' };
const GRID = 'rgba(0,0,0,0.07)';

// Status tokens are reserved for state — never reused as a series colour.
const TONE = {
  success: '#0f7b52',
  danger: '#c0362f',
  warning: '#b26a00',
  info: '#1f5fa8',
  default: INK.primary,
};
const toneColor = (tone) => TONE[tone] || TONE.default;

const TONE_ICON = {
  success: <CheckCircleFilled />,
  danger: <WarningFilled />,
  warning: <WarningFilled />,
  info: <InfoCircleFilled />,
};

/* ── Value formatting ──────────────────────────────────── */

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

const inr = (v, opts = {}) => num(v).toLocaleString('en-IN', { maximumFractionDigits: 0, ...opts });

// Indian short scale for axis ticks and cramped labels: ₹1.2Cr / ₹3.4L / ₹12.3K.
function compactINR(v) {
  const n = num(v);
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (abs >= 1e7) return `${sign}₹${(abs / 1e7).toFixed(abs >= 1e8 ? 0 : 1)}Cr`;
  if (abs >= 1e5) return `${sign}₹${(abs / 1e5).toFixed(abs >= 1e6 ? 0 : 1)}L`;
  if (abs >= 1e3) return `${sign}₹${(abs / 1e3).toFixed(abs >= 1e4 ? 0 : 1)}K`;
  return `${sign}₹${inr(abs)}`;
}

function compactNum(v) {
  const n = num(v);
  const abs = Math.abs(n);
  if (abs >= 1e7) return `${(n / 1e7).toFixed(1)}Cr`;
  if (abs >= 1e5) return `${(n / 1e5).toFixed(1)}L`;
  if (abs >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
  return inr(n, { maximumFractionDigits: 2 });
}

// Full-precision display used for headline figures, metric panels and table cells.
function formatValue(value, format, suffix, prefix) {
  if (value === null || value === undefined || value === '') return '—';
  switch (format) {
    case 'currency': return `₹${inr(value)}`;
    case 'percent': return `${num(value).toLocaleString('en-IN', { maximumFractionDigits: 1 })}%`;
    case 'weight': return `${num(value).toLocaleString('en-IN', { maximumFractionDigits: 3 })} g`;
    case 'number': return inr(value, { maximumFractionDigits: 2 });
    case 'text': return String(value);
    default: {
      if (typeof value === 'string') return value;
      const body = inr(value, { maximumFractionDigits: 2 });
      return `${prefix || ''}${body}${suffix ? ` ${suffix}` : ''}`;
    }
  }
}

// Axis-tick / tooltip formatter matched to the measure the chart is plotting.
const axisFormatter = (format) => (format === 'currency' ? compactINR : format === 'percent' ? ((v) => `${v}%`) : format === 'weight' ? ((v) => `${compactNum(v)}g`) : compactNum);
const tipFormatter = (format) => (v) => formatValue(v, format || 'number');

/* ── Small parts ───────────────────────────────────────── */

// Sparkline: the KPI's own measure over time, so the headline has a shape as well
// as a size. Deliberately unlabelled — the KPI value and the linked page carry the
// numbers; this only shows direction.
function Sparkline({ points = [], color }) {
  const vals = points.map(num).filter((v) => Number.isFinite(v));
  if (vals.length < 2) return null;
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const span = max - min || 1;
  const w = 72;
  const h = 26;
  const coords = vals.map((v, i) => [
    (i / (vals.length - 1)) * w,
    h - ((v - min) / span) * (h - 4) - 2,
  ]);
  const last = coords[coords.length - 1];
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{ flexShrink: 0, overflow: 'visible' }} aria-hidden="true">
      <polyline
        points={coords.map((p) => p.join(',')).join(' ')}
        fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      />
      {/* End marker carries a surface ring so it stays legible over the line. */}
      <circle cx={last[0]} cy={last[1]} r="4" fill={color} stroke={SURFACE} strokeWidth="2" />
    </svg>
  );
}

// "vs previous period" pill. `invert` flips the sentiment for cost-like measures,
// where growth is the bad news.
function DeltaPill({ delta }) {
  if (!delta || delta.direction === 'flat') return null;
  const up = delta.direction === 'up';
  const good = delta.invert ? !up : up;
  const color = good ? TONE.success : TONE.danger;
  const text = delta.percent === null || delta.percent === undefined
    ? compactINR(Math.abs(delta.value))
    : `${Math.abs(delta.percent)}%`;
  return (
    <Tooltip title={`${up ? 'Up' : 'Down'} ${compactNum(Math.abs(delta.value))} ${delta.label || ''}`.trim()}>
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 12, fontWeight: 600,
        color, background: `${color}14`, borderRadius: 20, padding: '1px 8px', whiteSpace: 'nowrap',
      }}>
        {up ? <ArrowUpOutlined style={{ fontSize: 10 }} /> : <ArrowDownOutlined style={{ fontSize: 10 }} />}
        {text}
      </span>
    </Tooltip>
  );
}

function StatusBanner({ status }) {
  if (!status) return null;
  const color = toneColor(status.tone);
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, padding: '7px 10px',
      borderRadius: 8, background: `${color}0F`, color, fontSize: 12, lineHeight: 1.4,
    }}>
      <span style={{ display: 'inline-flex', fontSize: 13 }}>{TONE_ICON[status.tone] || TONE_ICON.info}</span>
      <span style={{ fontWeight: 500 }}>{status.label}</span>
    </div>
  );
}

/* ── Card shell (title + deep link) ────────────────────── */

function WidgetShell({ title, route, extra, bodyPadding, children }) {
  const navigate = useNavigate();
  const open = route ? () => navigate(route) : null;

  return (
    <Card
      size="small"
      title={title ? <span style={{ fontSize: 13, fontWeight: 600, color: INK.primary }}>{title}</span> : null}
      style={{ height: '100%', borderRadius: 12 }}
      styles={{
        header: { minHeight: 40, borderBottom: `1px solid ${GRID}` },
        body: bodyPadding !== undefined ? { padding: bodyPadding } : undefined,
      }}
      extra={(
        <Space size={2}>
          {extra}
          {open && (
            <Tooltip title="Open the full report">
              <Button type="text" size="small" icon={<ArrowRightOutlined />} onClick={open} aria-label="Open the full report" />
            </Tooltip>
          )}
        </Space>
      )}
    >
      {children}
    </Card>
  );
}

/* ── KPI ───────────────────────────────────────────────── */

function KpiWidget({ title, route, data }) {
  const navigate = useNavigate();
  const color = toneColor(data?.tone);
  const value = formatValue(data?.value, data?.format, data?.suffix, data?.prefix);

  return (
    <Card
      size="small"
      style={{ height: '100%', borderRadius: 12, cursor: route ? 'pointer' : 'default' }}
      styles={{ body: { padding: 16 } }}
      onClick={route ? () => navigate(route) : undefined}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 6 }}>
        <Text style={{ fontSize: 12, color: INK.secondary }} ellipsis={{ tooltip: title }}>{title}</Text>
        <DeltaPill delta={data?.delta} />
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 10 }}>
        {/* Proportional figures on the hero number — tabular-nums only in tables. */}
        <div style={{ fontSize: 26, fontWeight: 650, lineHeight: 1.15, color, letterSpacing: '-0.01em', minWidth: 0 }}>
          <Tooltip title={value}><span>{value}</span></Tooltip>
        </div>
        <Sparkline points={data?.trend} color={color} />
      </div>
      {data?.hint && (
        <div style={{ marginTop: 6 }}>
          <Text style={{ fontSize: 11, color: INK.muted }} ellipsis={{ tooltip: data.hint }}>{data.hint}</Text>
        </div>
      )}
    </Card>
  );
}

/* ── Metrics panel ─────────────────────────────────────── */

function MetricsWidget({ title, route, data }) {
  const items = data?.items || [];
  if (!items.length) {
    return <WidgetShell title={title} route={route}><Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data" /></WidgetShell>;
  }

  return (
    <WidgetShell title={title} route={route}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: 1,
        background: GRID,
        border: `1px solid ${GRID}`,
        borderRadius: 10,
        overflow: 'hidden',
      }}>
        {items.map((it, i) => (
          <div key={`${it.label}-${i}`} style={{ background: SURFACE, padding: '10px 12px' }}>
            <Text style={{ fontSize: 11, color: INK.secondary, display: 'block' }} ellipsis={{ tooltip: it.label }}>
              {it.label}
            </Text>
            <div style={{ fontSize: 17, fontWeight: 650, color: toneColor(it.tone), lineHeight: 1.3, marginTop: 2 }}>
              <Tooltip title={formatValue(it.value, it.format)}>
                <span>{formatValue(it.value, it.format)}</span>
              </Tooltip>
            </div>
            {it.hint && (
              <Text style={{ fontSize: 10, color: INK.muted, display: 'block', marginTop: 1 }} ellipsis={{ tooltip: it.hint }}>
                {it.hint}
              </Text>
            )}
          </div>
        ))}
      </div>
      <StatusBanner status={data?.status} />
      {data?.footer && (
        <div style={{ marginTop: 8, textAlign: 'right' }}>
          <Text style={{ fontSize: 11, color: INK.muted }}>
            {data.footer.label}: {formatValue(data.footer.value, data.footer.format)}
          </Text>
        </div>
      )}
    </WidgetShell>
  );
}

/* ── Table ─────────────────────────────────────────────── */

function cellRender(col, value) {
  if (col.format === 'tag') return <Tag style={{ marginInlineEnd: 0 }}>{value}</Tag>;
  if (col.format && col.format !== 'text') {
    const n = num(value);
    const tone = col.format === 'currency' || col.format === 'percent'
      ? (n < 0 ? TONE.danger : INK.primary)
      : INK.primary;
    // tabular-nums here: these digits align down a column.
    return <span style={{ fontVariantNumeric: 'tabular-nums', color: tone }}>{formatValue(value, col.format)}</span>;
  }
  return value ?? '—';
}

function TableWidget({ title, route, data }) {
  const columns = (data?.columns || []).map((c) => ({
    title: c.title,
    dataIndex: c.key,
    key: c.key,
    align: c.align || 'left',
    ellipsis: true,
    render: (v) => cellRender(c, v),
  }));
  const rows = (data?.rows || []).map((r, i) => ({ key: i, ...r }));

  if (!columns.length || !rows.length) {
    return <WidgetShell title={title} route={route}><Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No rows" /></WidgetShell>;
  }

  return (
    <WidgetShell title={title} route={route} bodyPadding={0}>
      <Table
        size="small"
        columns={columns}
        dataSource={rows}
        pagination={false}
        scroll={{ x: 'max-content' }}
        summary={data?.summary ? () => (
          <Table.Summary fixed>
            <Table.Summary.Row style={{ background: '#fafafa' }}>
              {(data.columns || []).map((c, i) => (
                <Table.Summary.Cell key={c.key} index={i} align={c.align || 'left'}>
                  <strong>{cellRender(c, data.summary[c.key])}</strong>
                </Table.Summary.Cell>
              ))}
            </Table.Summary.Row>
          </Table.Summary>
        ) : undefined}
      />
    </WidgetShell>
  );
}

/* ── List ──────────────────────────────────────────────── */

function ListWidget({ title, route, data }) {
  const items = data?.items || [];
  return (
    <WidgetShell title={title} route={route} bodyPadding={items.length ? 0 : 16}>
      {items.length ? (
        <List
          size="small"
          dataSource={items}
          renderItem={(it) => (
            <List.Item extra={it.tag ? <Tag style={{ marginInlineEnd: 0 }}>{it.tag}</Tag> : null}>
              <List.Item.Meta
                title={<span style={{ fontSize: 13, color: INK.primary }}>{it.title}</span>}
                description={(it.subtitle || it.meta) ? (
                  <span style={{ fontSize: 12, color: INK.secondary }}>
                    {[it.subtitle, it.meta].filter(Boolean).join(' · ')}
                  </span>
                ) : null}
              />
            </List.Item>
          )}
        />
      ) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Nothing yet" />}
    </WidgetShell>
  );
}

/* ── Charts ────────────────────────────────────────────── */

// Dependency-free fallback, also used as the "view as table" twin's sibling when
// the chart library itself fails.
function SimpleBars({ series, format }) {
  const max = Math.max(1, ...series.map((s) => Math.abs(num(s.value))));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {series.slice(0, 12).map((s, i) => (
        <div key={i}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 3, gap: 8 }}>
            <Text ellipsis style={{ maxWidth: '65%', color: INK.secondary }}>
              {s.group ? `${s.label} · ${s.group}` : s.label}
            </Text>
            <span style={{ fontVariantNumeric: 'tabular-nums', color: INK.primary, fontWeight: 500 }}>
              {formatValue(s.value, format)}
            </span>
          </div>
          <div style={{ background: GRID, borderRadius: 4, height: 8 }}>
            <div style={{
              width: `${Math.round((Math.abs(num(s.value)) / max) * 100)}%`,
              background: SERIES[0], height: 8, borderRadius: 4,
            }} />
          </div>
        </div>
      ))}
    </div>
  );
}

class ChartErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { failed: false }; }
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch(e) { console.warn('[WidgetCard] chart render failed, using fallback', e?.message || e); }
  render() { return this.state.failed ? this.props.fallback : this.props.children; }
}

// Part-to-whole reads at a glance only up to ~6 slices; the tail folds into
// "Other" rather than cycling hues past the palette.
function foldTail(series, max = 6) {
  if (series.length <= max) return series;
  const sorted = [...series].sort((a, b) => Math.abs(num(b.value)) - Math.abs(num(a.value)));
  const head = sorted.slice(0, max - 1);
  const rest = sorted.slice(max - 1).reduce((s, r) => s + num(r.value), 0);
  return [...head, { label: 'Other', value: Math.round(rest * 100) / 100 }];
}

function RichChart({ chartType, series, format, grouped, stacked, height }) {
  const data = series.map((s) => ({
    label: String(s.label ?? ''),
    value: num(s.value),
    ...(s.group ? { group: String(s.group) } : {}),
  }));

  const fmtTick = axisFormatter(format);
  const fmtTip = tipFormatter(format);

  const base = {
    data,
    height,
    autoFit: true,
    animate: false,
    scale: { color: { range: SERIES } },
    tooltip: { items: [{ channel: 'y', valueFormatter: fmtTip }] },
    // Recessive chrome: hairline solid grid, muted labels, no axis titles.
    axis: {
      x: { title: null, labelFontSize: 11, labelFill: INK.secondary, line: false, tick: false, labelAutoRotate: true, labelAutoHide: true },
      y: { title: null, labelFontSize: 11, labelFill: INK.secondary, labelFormatter: fmtTick, line: false, tick: false, grid: true, gridStroke: GRID, gridLineWidth: 1 },
    },
    // A legend is the dependable identity channel; a single series doesn't need one.
    legend: grouped
      ? { color: { position: 'bottom', layout: { justifyContent: 'center' }, itemLabelFontSize: 11, itemLabelFill: INK.secondary } }
      : false,
  };

  const colorProps = grouped ? { colorField: 'group' } : { color: SERIES[0] };

  switch (chartType) {
    case 'line':
      return (
        <Line {...base} {...colorProps} xField="label" yField="value"
          style={{ lineWidth: 2, lineCap: 'round', lineJoin: 'round' }}
          point={{ sizeField: 4, style: { stroke: SURFACE, lineWidth: 2 } }} />
      );
    case 'area':
      return (
        <Area {...base} {...colorProps} xField="label" yField="value"
          style={{ fillOpacity: 0.12, lineWidth: 2 }}
          line={{ style: { lineWidth: 2 } }} />
      );
    case 'column':
      return (
        <Column {...base} {...colorProps} xField="label" yField="value"
          group={grouped && !stacked} stack={!!stacked}
          // ≤24px bars with a 4px rounded cap; the 2px surface gap does the separating.
          style={{ maxWidth: 24, radiusTopLeft: 4, radiusTopRight: 4, insetLeft: 1, insetRight: 1, stroke: SURFACE, lineWidth: stacked ? 2 : 0 }} />
      );
    case 'bar':
      return (
        <Bar {...base} {...colorProps} xField="label" yField="value"
          group={grouped && !stacked} stack={!!stacked}
          axis={{ ...base.axis, x: { ...base.axis.x, grid: false }, y: { ...base.axis.y, labelFormatter: fmtTick } }}
          style={{ maxWidth: 24, radiusTopRight: 4, radiusBottomRight: 4, insetTop: 1, insetBottom: 1 }} />
      );
    case 'pie':
    case 'donut':
      return (
        <Pie {...base} data={foldTail(data)} angleField="value" colorField="label"
          innerRadius={chartType === 'donut' ? 0.62 : 0}
          label={{ text: 'label', position: 'outside', fontSize: 11, fill: INK.secondary, connector: true }}
          legend={{ color: { position: 'right', layout: { justifyContent: 'center' }, itemLabelFontSize: 11, itemLabelFill: INK.secondary, maxRows: 8 } }}
          style={{ stroke: SURFACE, lineWidth: 2, radius: 0.98 }} />
      );
    default:
      return <SimpleBars series={series} format={format} />;
  }
}

// The table twin every chart carries — the WCAG-clean way to read the same values.
function ChartTable({ series, format, grouped }) {
  const columns = [
    { title: grouped ? 'Point' : 'Label', dataIndex: 'label', key: 'label', ellipsis: true },
    ...(grouped ? [{ title: 'Series', dataIndex: 'group', key: 'group', ellipsis: true }] : []),
    {
      title: 'Value',
      dataIndex: 'value',
      key: 'value',
      align: 'right',
      render: (v) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatValue(v, format)}</span>,
    },
  ];
  const total = series.reduce((s, r) => s + num(r.value), 0);
  return (
    <Table
      size="small"
      columns={columns}
      dataSource={series.map((s, i) => ({ key: i, ...s }))}
      pagination={false}
      scroll={{ y: 220 }}
      summary={() => (
        <Table.Summary fixed>
          <Table.Summary.Row style={{ background: '#fafafa' }}>
            <Table.Summary.Cell index={0} colSpan={grouped ? 2 : 1}><strong>Total</strong></Table.Summary.Cell>
            <Table.Summary.Cell index={1} align="right">
              <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{formatValue(total, format)}</strong>
            </Table.Summary.Cell>
          </Table.Summary.Row>
        </Table.Summary>
      )}
    />
  );
}

function ChartWidget({ title, route, chartType, data, span }) {
  const [asTable, setAsTable] = useState(false);
  const series = useMemo(() => data?.series || [], [data]);
  const format = data?.meta?.format;
  const grouped = useMemo(() => series.some((s) => s.group), [series]);
  const stacked = !!data?.meta?.stacked;
  // Wide widgets get a taller plot; the container includes the axis band so the
  // card never grows a nested scrollbar just to show tick labels.
  const height = span >= 24 ? 300 : 240;

  if (!series.length) {
    return <WidgetShell title={title} route={route}><Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data" /></WidgetShell>;
  }

  return (
    <WidgetShell
      title={title}
      route={route}
      bodyPadding={asTable ? 0 : 12}
      extra={(
        <Tooltip title={asTable ? 'View as chart' : 'View as table'}>
          <Button
            type="text" size="small"
            icon={asTable ? <BarChartOutlined /> : <TableOutlined />}
            onClick={() => setAsTable((v) => !v)}
            aria-label={asTable ? 'View as chart' : 'View as table'}
          />
        </Tooltip>
      )}
    >
      {asTable ? (
        <ChartTable series={series} format={format} grouped={grouped} />
      ) : (
        <ChartErrorBoundary fallback={<SimpleBars series={series} format={format} />}>
          <RichChart
            chartType={chartType}
            series={series}
            format={format}
            grouped={grouped}
            stacked={stacked}
            height={height}
          />
        </ChartErrorBoundary>
      )}
    </WidgetShell>
  );
}

/* ── Action queue (approve / reject in place) ──────────── */

const PRIORITY_TONE = { urgent: TONE.danger, critical: TONE.danger, high: TONE.warning, normal: INK.muted, low: INK.muted };

// The one widget that writes: each row carries the approval-instance id, and
// Approve / Reject post to the same endpoint the My Approvals page uses — the
// engine re-verifies the viewer is a current-step approver, so this is a
// shortcut to the action, never a way around its checks. A comment is required,
// exactly as on the page.
function ActionQueueWidget({ title, route, data, onRefresh }) {
  const navigate = useNavigate();
  const [messageApi, contextHolder] = message.useMessage();
  const [modal, setModal] = useState({ open: false, action: null, row: null, comment: '', loading: false });
  // Rows already actioned this render — dropped immediately so the card can't
  // offer the same approval twice while the dashboard refetches.
  const [done, setDone] = useState({});

  const items = (data?.items || []).filter((it) => !done[it.id]);
  const total = Math.max(0, (data?.total || 0) - Object.keys(done).length);
  const target = route || '/approvals/my-approvals';

  const openAction = (row, action) => setModal({ open: true, action, row, comment: '', loading: false });

  // `payload` is the decision-time input the modal collected (bank UTRs for a
  // payment approval); undefined for every request that needs none.
  const submit = async (payload) => {
    if (!modal.comment.trim()) { messageApi.warning('A comment is required'); return; }
    setModal((m) => ({ ...m, loading: true }));
    try {
      await progressApprovalInstance({ instanceId: modal.row.id, action: modal.action, comment: modal.comment, payload });
      setDone((d) => ({ ...d, [modal.row.id]: true }));
      setModal({ open: false, action: null, row: null, comment: '', loading: false });
      messageApi.success(`${modal.row.reference} ${modal.action === 'approved' ? 'approved' : 'rejected'}`);
      onRefresh?.();
    } catch (err) {
      setModal((m) => ({ ...m, loading: false }));
      messageApi.error(err?.response?.data?.message || 'Action failed');
    }
  };

  const openRow = async (row) => {
    try { await markApprovalViewed(row.id); } catch { /* non-fatal */ }
    navigate(`/approvals/instance/${row.id}`);
  };

  return (
    <Card
      size="small"
      style={{ height: '100%', borderRadius: 12, display: 'flex', flexDirection: 'column' }}
      styles={{ header: { minHeight: 40, borderBottom: `1px solid ${GRID}` }, body: { padding: 0, flex: 1 } }}
      title={(
        <Space size={8}>
          <span style={{ fontSize: 13, fontWeight: 600, color: INK.primary }}>{title}</span>
          {total > 0 && <Badge count={total} overflowCount={99} style={{ backgroundColor: TONE.warning }} />}
        </Space>
      )}
    >
      {contextHolder}

      {items.length === 0 ? (
        <div style={{ padding: '28px 16px', textAlign: 'center' }}>
          <InboxOutlined style={{ fontSize: 26, color: TONE.success }} />
          <div style={{ marginTop: 8, color: INK.secondary, fontSize: 13 }}>Nothing waiting on you</div>
        </div>
      ) : (
        <div>
          {items.map((it, i) => (
            <div
              key={it.id}
              style={{
                padding: '10px 12px',
                borderTop: i === 0 ? 'none' : `1px solid ${GRID}`,
                display: 'flex', gap: 10, alignItems: 'flex-start', flexWrap: 'wrap',
              }}
            >
              <div style={{ flex: '1 1 190px', minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  <Text strong style={{ fontSize: 13, color: INK.primary }} ellipsis={{ tooltip: it.reference }}>
                    {it.reference}
                  </Text>
                  {it.amount !== null && it.amount !== undefined && (
                    <span style={{ fontSize: 12, fontWeight: 600, color: TONE.info, fontVariantNumeric: 'tabular-nums' }}>
                      ₹{inr(it.amount)}
                    </span>
                  )}
                  {it.priority && it.priority !== 'normal' && (
                    <Tag color={it.priority === 'low' ? 'default' : 'red'} style={{ marginInlineEnd: 0, fontSize: 10, lineHeight: '16px' }}>
                      {it.priority.toUpperCase()}
                    </Tag>
                  )}
                </div>
                <Text style={{ fontSize: 11, color: INK.secondary, display: 'block' }} ellipsis={{ tooltip: `${it.type}${it.note ? ` — ${it.note}` : ''}` }}>
                  {it.type}{it.requestedBy ? ` · ${it.requestedBy}` : ''}
                </Text>
                <Text style={{ fontSize: 11, color: it.overdue ? TONE.danger : INK.muted, display: 'block' }}>
                  {it.step} · waiting {it.waiting}
                </Text>
              </div>

              <Space size={4} style={{ flexShrink: 0 }}>
                <Tooltip title="Open the full request">
                  <Button size="small" icon={<EyeOutlined />} onClick={() => openRow(it)} aria-label="View request" />
                </Tooltip>
                <PermissionButton
                  buttonType={ActionType.APPROVE} module={ModuleType.WORKFLOW}
                  type="primary" size="small" icon={<CheckOutlined />}
                  onClick={() => openAction(it, 'approved')}
                >
                  Approve
                </PermissionButton>
                <PermissionButton
                  buttonType={ActionType.REJECT} module={ModuleType.WORKFLOW}
                  danger size="small" icon={<CloseOutlined />}
                  onClick={() => openAction(it, 'rejected')}
                >
                  Reject
                </PermissionButton>
              </Space>
            </div>
          ))}
        </div>
      )}

      {/* See-all: straight through to the full inbox. */}
      <div style={{ borderTop: `1px solid ${GRID}`, padding: 10 }}>
        <Button block type="primary" ghost icon={<ArrowRightOutlined />} onClick={() => navigate(target)}>
          {total > items.length
            ? `See all ${total} in My Approvals`
            : 'Open My Approvals'}
        </Button>
      </div>

      <ApprovalActionModal
        visible={modal.open}
        actionType={modal.action}
        comment={modal.comment}
        onChange={(e) => setModal((m) => ({ ...m, comment: e.target.value }))}
        onOk={submit}
        onCancel={() => setModal({ open: false, action: null, row: null, comment: '', loading: false })}
        loading={modal.loading}
        instanceId={modal.row?.id}
      />
    </Card>
  );
}

/* ── Shortcut ──────────────────────────────────────────── */

function ShortcutWidget({ title, data }) {
  const navigate = useNavigate();
  const IconComp = (data?.icon && Icons[data.icon]) || Icons.ArrowRightOutlined;
  return (
    <Card size="small" style={{ height: '100%', borderRadius: 12, display: 'flex', alignItems: 'center' }}>
      <Button type="primary" ghost block size="large" icon={<IconComp />}
        onClick={() => data?.route && navigate(data.route)}>
        {data?.label || title || 'Open'}
      </Button>
    </Card>
  );
}

/* ── Entry point ───────────────────────────────────────── */

// Actionable widgets each drive a different business flow, so they dispatch by
// widgetKey rather than sharing one renderer.
const ACTION_RENDERERS = {
  'myApprovals.inbox': ActionQueueWidget,
  'eod.control': EodControlWidget,
  'ibct.inbox': IbctInboxWidget,
};

export default function WidgetCard({ widget, onRefresh }) {
  const { kind, chartType, title, data, route, span, widgetKey } = widget || {};
  if (kind === 'action') {
    const Renderer = ACTION_RENDERERS[widgetKey];
    // `widgetId` lets a widget re-render just itself (branch switch, post-action).
    return Renderer
      ? <Renderer widgetId={widget.id} title={title} route={route} data={data} onRefresh={onRefresh} />
      : <Card size="small" title={title}><Empty description="Unknown action widget" /></Card>;
  }
  switch (kind) {
    case 'kpi': return <KpiWidget title={title} route={route} data={data} />;
    case 'metrics': return <MetricsWidget title={title} route={route} data={data} />;
    case 'table': return <TableWidget title={title} route={route} data={data} />;
    case 'list': return <ListWidget title={title} route={route} data={data} />;
    case 'chart': return <ChartWidget title={title} route={route} chartType={chartType} data={data} span={span} />;
    case 'shortcut': return <ShortcutWidget title={title} data={data} />;
    default: return <Card size="small" title={title}><Empty description="Unknown widget" /></Card>;
  }
}
